import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { _2dMapCombinedQuote } from '@larki/shared/type/order.v2';
import { QuoteInputs } from '@larki/shared/validations/order.v2.validation';

// Lets commit send what the quote was computed from (asMember added at commit).
export type StoredQuoteInputs = Omit<QuoteInputs, 'asMember'>;

type OrderV2State = {
  quotes: Map<
    number,
    {
      freeUserQuote: _2dMapCombinedQuote;
      memberQuote: _2dMapCombinedQuote;
      quoteInputs?: StoredQuoteInputs;
    }
  >; // jobId -> quote
  quoteLoading: Map<number, boolean>; // jobId -> loading
  orderProjectId: number | null;
  customerSoftware: string | null;
  comments: string | null;
};

const initialState: OrderV2State = {
  quotes: new Map(),
  quoteLoading: new Map(),
  orderProjectId: null,
  customerSoftware: null,
  comments: null,
};

export const orderV2Slice = createSlice({
  name: 'orderv2',
  initialState,
  reducers: {
    setQuotes: (
      state,
      action: PayloadAction<{
        jobId: number;
        freeUserQuote: _2dMapCombinedQuote;
        memberQuote: _2dMapCombinedQuote;
        quoteInputs?: StoredQuoteInputs;
      }>
    ) => {
      state.quotes.set(action.payload.jobId, action.payload);
      state.quoteLoading.set(action.payload.jobId, false);
    },
    // Overwrite just the committed variant so totals match what was persisted.
    applyRepricedQuote: (
      state,
      action: PayloadAction<{
        jobId: number;
        asMember: boolean;
        repriced: _2dMapCombinedQuote;
      }>
    ) => {
      const entry = state.quotes.get(action.payload.jobId);
      if (!entry) return;
      if (action.payload.asMember) {
        entry.memberQuote = action.payload.repriced;
      } else {
        entry.freeUserQuote = action.payload.repriced;
      }
    },
    clearJobQuote: (state, action: PayloadAction<number>) => {
      state.quotes.delete(action.payload);
      state.quoteLoading.set(action.payload, true);
    },
    setOrderProjectId: (state, action: PayloadAction<number | null>) => {
      state.orderProjectId = action.payload;
    },
    setCustomerSoftware: (
      state,
      action: PayloadAction<OrderV2State['customerSoftware']>
    ) => {
      state.customerSoftware = action.payload;
    },
    setQuoteLoading: (
      state,
      action: PayloadAction<{ jobId: number; loading: boolean }>
    ) => {
      state.quoteLoading.set(action.payload.jobId, action.payload.loading);
    },
    setComments: (state, action: PayloadAction<string | null>) => {
      state.comments = action.payload;
    },
  },
});

export const { setQuotes, applyRepricedQuote, clearJobQuote, setQuoteLoading } =
  orderV2Slice.actions;
export const orderV2Actions = orderV2Slice.actions;
